import { memo, useCallback, useMemo, useState } from 'react';
import { Table, TableProps } from 'antd';
import { ExpandableConfig, SorterResult, SortOrder } from 'antd/es/table/interface';

import { useStore } from 'zustand';
import { OrderDirection, DEFAULT_TIMESHEETS_SORTING, timesheetsStore } from 'entities/timesheet';
import { EmptyGroup } from 'shared/ui/EmptyGroup';
import { getAllExpandableKeys } from '../../lib/getAllExpandableKeys';
import { mapFilterDTOToTableFilters } from '../../lib/mapFilterDTOToTableFilters';
import { mapFiltersToFilterDTO } from '../../lib/mapFiltersToFilterDTO';
import { TimesheetTableRow, CustomColumn } from '../../TimesheetsTable.types';

import { ExpandIcon } from '../ExpandIcon';

import styles from './TimessheetsTable.module.scss';

const TimesheetsTable = ({
  tableData,
  columnsVisibilityMap,
  allColumns,
  isExpanded,
  isLoading,
}: {
  tableData: TimesheetTableRow[];
  columnsVisibilityMap: Record<string, boolean>;
  allColumns: CustomColumn<TimesheetTableRow>[];
  isExpanded: boolean;
  isLoading: boolean;
}): React.ReactElement => {
  const [expandedList, setExpandedList] = useState<string[]>([]);

  const setOrder = useStore(timesheetsStore, (state) => state.setOrders);
  const setColumnFilters = useStore(timesheetsStore, (state) => state.setColumnFilters);
  const columnFilters = useStore(timesheetsStore, (state) => state.columnFilters);

  const tableFilters = useMemo(() => mapFilterDTOToTableFilters(columnFilters), [columnFilters]);

  const columnsWithFilters = useMemo(() => {
    return allColumns
      .filter((col) => columnsVisibilityMap[col.key])
      .map((col) => ({
        ...col,
        filteredValue: tableFilters[col.key as string] ?? null,
      }));
  }, [allColumns, columnsVisibilityMap, tableFilters]);

  const initialExpandedList = useMemo(() => {
    if (isExpanded && tableData.length > 0) {
      return getAllExpandableKeys(tableData);
    }
    return [];
  }, [isExpanded, tableData]);

  const onExpand = useCallback((expanded: boolean, record: TimesheetTableRow['key'][]): void => {
    if (!expanded) {
      setExpandedList((prev) => prev.filter((item) => !record.includes(item)));
    } else {
      setExpandedList((prev) => [...prev, ...record]);
    }
  }, []);

  const finalExpandedList = useMemo(() => {
    return isExpanded ? initialExpandedList : expandedList;
  }, [isExpanded, expandedList, initialExpandedList]);

  const expandConfig: ExpandableConfig<TimesheetTableRow> = useMemo(() => {
    return {
      expandIcon: ExpandIcon,
      onExpand: (expanded, record) => {
        onExpand(expanded, [record.key]);
        if ('accountName' in record) {
          record.children.forEach((project) => {
            onExpand(expanded, [project.key]);
          });
        }
      },
      expandedRowKeys: finalExpandedList,
    };
  }, [onExpand, finalExpandedList]);

  const parseSortOrder = (sortOrder: SortOrder): OrderDirection => {
    return sortOrder === 'ascend' ? 'ASC' : 'DESC';
  };

  const handleSortingChange = (fieldName?: React.Key | readonly React.Key[], order?: SortOrder): void => {
    if (!fieldName || !order) {
      setOrder(DEFAULT_TIMESHEETS_SORTING);
      return;
    }
    const parsedOrder = parseSortOrder(order);
    const orders = [{ fieldName: fieldName as string, orderDirection: parsedOrder }];
    setOrder(orders);
  };

  const handleFilterChange = (filters: Record<string, string[] | null>): void => {
    if (filters) {
      const mappedFilters = mapFiltersToFilterDTO(filters);
      setColumnFilters(mappedFilters);
    }
  };

  const onChange: TableProps<TimesheetTableRow>['onChange'] = (_, filters, sorter, extra) => {
    if (extra.action === 'sort') {
      const currentSorter = sorter as SorterResult<TimesheetTableRow>;
      handleSortingChange(currentSorter.columnKey, currentSorter.order);
    }
    if (extra.action === 'filter') {
      const currentFilters = filters as Record<string, string[] | null>;
      handleFilterChange(currentFilters);
    }
  };

  return (
    <Table
      rowKey={(record) => record.key}
      size="small"
      dataSource={tableData}
      columns={columnsWithFilters}
      showSorterTooltip={false}
      onChange={onChange}
      pagination={false}
      loading={isLoading}
      className={styles.table}
      bordered={false}
      expandable={expandConfig}
      rowHoverable={false}
      scroll={{ y: 310 }}
      locale={{
        emptyText: isLoading ? null : <EmptyGroup />,
      }}
    />
  );
};

export default memo(TimesheetsTable);
